import type {
  ClientPayNormalisationRunWire,
  SummaryPreviewRowWire,
  SummaryPreviewTotalsWire,
} from "../types/clientPayNormalisationApi";
import type {
  NormalisationDetailPreviewRow,
  NormalisationIssue,
  NormalisationRunResult,
  NormalisationSummaryTotals,
} from "../types/normalisationRun";
import { formatMoneyAmount, formatOptionalMoneyAmount } from "./formatMoney";

function mapSummaryTotals(
  totals: SummaryPreviewTotalsWire | null | undefined,
): NormalisationSummaryTotals {
  return {
    grossPay: formatOptionalMoneyAmount(totals?.grossPay),
    gross: formatMoneyAmount(totals?.grossTaxable),
    tax: formatMoneyAmount(totals?.paye),
    nettPay: formatMoneyAmount(totals?.netPay),
    studentLoan: formatOptionalMoneyAmount(totals?.studentLoan),
    ksEe: formatMoneyAmount(totals?.ksEe),
    ksEr: formatMoneyAmount(totals?.ksErGross),
    esct: formatMoneyAmount(totals?.ksErEsct),
    otherSuperEe: formatOptionalMoneyAmount(totals?.otherSuperEe),
    otherSuperEr: formatOptionalMoneyAmount(totals?.otherSuperEr),
    totalDeductions: "0.00",
  };
}

function mapDetailPreviewRow(
  row: SummaryPreviewRowWire,
  previewIndex: number,
): NormalisationDetailPreviewRow {
  return {
    payslipBlockIndex: row.blockIndex ?? previewIndex + 1,
    employeeCode: row.employeeCode,
    taxCode: row.taxCode?.trim() ?? "",
    grossPay: formatOptionalMoneyAmount(row.grossPay),
    gross: formatMoneyAmount(row.grossTaxable),
    tax: formatMoneyAmount(row.paye),
    nettPay: formatMoneyAmount(row.netPay),
    studentLoan: formatOptionalMoneyAmount(row.studentLoan),
    ksEe: formatMoneyAmount(row.ksEe),
    ksEr: formatMoneyAmount(row.ksErGross),
    esct: formatMoneyAmount(row.ksErEsct),
    otherSuperEe: formatOptionalMoneyAmount(row.otherSuperEe),
    otherSuperEr: formatOptionalMoneyAmount(row.otherSuperEr),
  };
}

function mapIssue(issue: ClientPayNormalisationRunWire["issues"][number]): NormalisationIssue {
  return {
    code: issue.code,
    message: issue.message,
    employeeCode: issue.employeeCode ?? undefined,
    payslipBlockIndex: issue.blockIndex ?? undefined,
  };
}

/**
 * Issue code Pacific.Agentic.Api #1098 emits for each payslip block that could
 * not be parsed (warning severity — run-blocking issues never carry a block).
 */
const BLOCK_PARSE_FAILED_CODE = "BLOCK_PARSE_FAILED";

/**
 * Per-slip failure count: the API reports unreadable blocks as `BLOCK_PARSE_FAILED`
 * warnings, so `blockingIssueCount` alone under-counts them (it stays 0 while
 * slips are still dropped from the summary preview). Preview rows may be truncated
 * for CN-06, so never infer failures from payslipBlockCount − preview length.
 */
function countPayslipBlocksFailed(run: ClientPayNormalisationRunWire): number {
  return run.issues.filter((issue) => issue.code === BLOCK_PARSE_FAILED_CODE).length;
}

/**
 * Maps Pacific.Agentic.Api #1098 run JSON to portal UI run models (CN-06–CN-08).
 */
export function mapNormalisationRunFromApi(
  run: ClientPayNormalisationRunWire,
): NormalisationRunResult {
  if (run.status === "blocked") {
    return {
      outcome: "blocked",
      runId: run.runId,
      importSourceId: run.importSource,
      parserProfileId: run.parserProfileId,
      issues: run.issues.map(mapIssue),
    };
  }

  const preview = run.summaryPreview;
  const detailPreview = (preview?.rows ?? []).map((row, index) =>
    mapDetailPreviewRow(row, index),
  );
  const payslipBlocksFailed = countPayslipBlocksFailed(run);
  const payslipBlocksTotal = run.payslipBlockCount;
  const payslipBlocksParsed = Math.max(
    0,
    payslipBlocksTotal - payslipBlocksFailed,
  );
  // Summary totals cover successfully parsed blocks; preview rows may be truncated for CN-06.
  const employeeSummaryRows = payslipBlocksParsed;

  const isPartial =
    run.warningCount > 0 ||
    payslipBlocksFailed > 0 ||
    run.issues.length > 0;

  const summaryTotals = mapSummaryTotals(preview?.totals);

  return {
    outcome: isPartial ? "partial" : "complete",
    runId: run.runId,
    importSourceId: run.importSource,
    parserProfileId: run.parserProfileId,
    payslipBlocksTotal,
    payslipBlocksParsed,
    payslipBlocksFailed,
    employeeSummaryRows,
    warningCount: run.warningCount,
    summaryTotals,
    detailPreview,
    availableArtifacts: run.availableArtifacts,
    issues: isPartial ? run.issues.map(mapIssue) : undefined,
  };
}
